import { logger } from '@automata/utils';

import * as LabwareErrors from '../features/shared/labwareErrors';
import { stringifySlots } from '../features/shared/utils';
import {
  INVALID_TIME_CONSTRAINT,
  WorkflowSerializationError,
} from '../features/workflowJsonSelectors';
import * as TaskErrors from '../features/workflowJsonSelectors/taskErrors';
import type { InstrumentForLocation } from '../types';
import { supportsSlotRanges } from '../utils/instrumentCapabilities';

export type AnnotatedError<T> = {
  title: string;
  description: string;
  inputError?: string;
  error: T;
};

/** Transforms serialization errors to user-facing copy with title and description */
export const errorsToUserCopy = (
  errors: WorkflowSerializationError[],
  instrumentsById: Record<string, InstrumentForLocation> = {}
): AnnotatedError<WorkflowSerializationError>[] => {
  return errors.map((error) => {
    switch (error.message) {
      case LabwareErrors.INVALID_LABWARE_DEFINITION_URI: {
        return {
          title: 'Invalid labware definition',
          description: 'An invalid labware definition was specified.',
          error,
        };
      }
      case LabwareErrors.MISSING_LABWARE_DEFINITION_URI: {
        return {
          title: 'Missing labware definition',
          description: 'A labware is missing its labware definition.',
          error,
        };
      }
      case LabwareErrors.MISSING_START_LOCATION: {
        return {
          title: 'Missing labware placement',
          description:
            'Make sure all labware have their starting location defined.',
          error,
        };
      }
      case LabwareErrors.LABWARE_START_LOCATION_SLOT_CAPACITY_EXCEEDED: {
        return {
          title:
            'Labware slot capacity exceeded in starting location assignments',
          description:
            'Ensure that the total number of labware starting locations assigned to a device does not exceed its capacity.',
          inputError: `Invalid range. Instrument capacity: ${error.capacity}`,
          error,
        };
      }
      case LabwareErrors.LABWARE_FINAL_LOCATION_SLOT_CAPACITY_EXCEEDED: {
        return {
          title: 'Labware slot capacity exceeded in final location resolution',
          description:
            'Ensure that the total number of labware final locations resolving to a device does not exceed its capacity.',
          inputError: `Invalid range. Instrument capacity: ${error.capacity}`,
          error,
        };
      }
      case LabwareErrors.LABWARE_START_LOCATION_SLOT_INVALID: {
        const instrument = error.instrumentId
          ? instrumentsById[error.instrumentId]
          : undefined;
        const isSlotRange = instrument
          ? supportsSlotRanges(instrument.driverName)
          : false;
        return {
          title: 'Invalid slot input',
          description: isSlotRange
            ? 'Invalid format. Example: 1, 5, 21-30'
            : 'Invalid format. Example: 1, 5',
          inputError: 'invalid slot format',
          error,
        };
      }
      case LabwareErrors.LABWARE_START_LOCATION_SELF_COLLISION: {
        return {
          title: 'Labware starting slots overlap',
          description:
            'Some of the starting location slots or slot ranges overlap in a single labware.',
          error,
        };
      }
      case LabwareErrors.LABWARE_FINAL_LOCATION_SELF_COLLISION: {
        return {
          title: 'Labware final slots overlap',
          description:
            'Some of the final location slots or slot ranges overlap in a single labware.',
          error,
        };
      }
      case LabwareErrors.LABWARE_START_LOCATION_SLOT_COLLISION: {
        return {
          title: 'Labware starting slots collision',
          description:
            'Collision with starting slots of one or more other labware.',
          inputError: `Conflict: slots ${stringifySlots(
            error.slotsInUse ?? []
          )} already assigned`,
          error,
        };
      }
      case LabwareErrors.LABWARE_FINAL_LOCATION_SLOT_COLLISION: {
        return {
          title: 'Labware final slots collision',
          description:
            'Collision with final slots of one or more other labware.',
          inputError: `Conflict: slots ${stringifySlots(
            error.slotsInUse ?? []
          )} already assigned`,
          error,
        };
      }
      case LabwareErrors.LABWARE_SLOT_COLLISION: {
        return {
          title: 'Labware slots collision',
          description:
            'Collision with labware positions on the instrument deck.',
          inputError: 'slot collision',
          error,
        };
      }
      case INVALID_TIME_CONSTRAINT: {
        return {
          title: 'Invalid time constraint',
          description:
            'Ensure tasks share common labware and that max duration is greater than the total duration of all tasks.',
          error,
        };
      }
      case TaskErrors.CIRCULAR_GRAPH: {
        return {
          title: 'Cyclical graph',
          description: 'The graph contains circular dependencies.',
          error,
        };
      }
      case TaskErrors.MISSING_INSTRUMENT: {
        return {
          title: 'Missing instrument',
          description: 'A task has no instrument specified.',
          error,
        };
      }
      case TaskErrors.MISSING_TIME_ESTIMATE: {
        return {
          title: 'Missing task duration',
          description: 'Verify that timings are provided for all tasks.',
          error,
        };
      }
      case TaskErrors.INVALID_TASK_FIELD_VALUES: {
        return {
          title: 'Invalid task field values',
          description: 'Ensure all fields are filled in correctly.',
          error,
        };
      }
      case TaskErrors.NO_LABWARE_FOR_TASK: {
        return {
          title: 'Task has no labware',
          description: 'Ensure that each task has a labware assigned.',
          error,
        };
      }
      case TaskErrors.NO_TASK_PROPERTIES: {
        return {
          title: 'Missing task properties',
          description: 'A task was referenced that does not exist.',
          error,
        };
      }
      case TaskErrors.MINIMUM_GRAPH_REQUIREMENTS: {
        return {
          title: 'Nothing to plan',
          description: 'Ensure that at least a task is assigned to a labware.',
          error,
        };
      }
      case TaskErrors.INVALID_TASK_DEPENDENCIES: {
        return {
          title: 'Invalid task dependency',
          description:
            'For each dependency, ensure both tasks are connected to different labware.',
          error,
        };
      }
      case TaskErrors.TASK_SLOT_FILLING_RULE_INVALID_SLOT_STRING: {
        const instrument = error.instrumentId
          ? instrumentsById[error.instrumentId]
          : undefined;
        const isSlotRange = instrument
          ? supportsSlotRanges(instrument.driverName)
          : false;
        return {
          title: 'Invalid slots',
          description: isSlotRange
            ? 'Invalid format. Example: 1, 5, 21-30'
            : 'Invalid format. Example: 1, 5',
          inputError: 'invalid slot format',
          error,
        };
      }
      case 'LABWARE_SLOT_RANGE_GLOBAL_SIZE_MISMATCH': {
        return {
          title: 'Slot range size mismatch',
          description:
            'For a given labware, all slot ranges used across tasks must have the same length.',
          inputError: 'range sizes must match',
          error,
        };
      }
      case 'CONSECUTIVE_TASK_SLOT_MISMATCH': {
        return {
          title: 'End/Start slots do not match',
          description:
            'The end slots of the previous task must match the start slots of the next task for the same labware.',
          inputError: 'end/start mismatch',
          error,
        };
      }
      default: {
        error satisfies never;
        logger.warn(
          `errorsToUserCopy got error with no copy: ${JSON.stringify(error)}`
        );
        const fallback = (error as WorkflowSerializationError).message;
        return { title: fallback, description: fallback, error };
      }
    }
  });
};
